/**
 * SimulationConfirmationModal — final guardrail before a SuperAdmin triggers
 * a real-pipeline simulation (Session 2026-04-18 Q1 blast-radius disclosure).
 *
 * The confirm button stays disabled until the admin types the region name
 * VERBATIM — this is the type-to-confirm pattern used across destructive
 * admin flows to prevent muscle-memory fat-fingers.
 *
 * 014-alerts / US7 / T133.
 */
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  regionName: string;
  alertType: string;
  durationMinutes: number;
  isSubmitting?: boolean;
}

export function SimulationConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  regionName,
  alertType,
  durationMinutes,
  isSubmitting = false,
}: Props) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');

  // Reset the typed confirmation every time the modal reopens so leftover text
  // from a previous attempt can't arm the confirm button by accident.
  useEffect(() => {
    if (isOpen) setTyped('');
  }, [isOpen]);

  const canConfirm = typed.trim() === regionName.trim() && !isSubmitting;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent data-testid="simulation-confirmation-modal">
        <ModalHeader>
          {t('admin.alerts.simulate.confirmTitle', 'Confirm real-pipeline simulation')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Alert status="warning" variant="left-accent">
              <AlertIcon />
              <Text fontSize="sm">
                {t(
                  'admin.alerts.simulate.blastRadiusWarning',
                  'This will play the alarm-start audio on every enabled customer location in the selected region. Customers will see a "Test alert" badge.',
                )}
              </Text>
            </Alert>

            <Box bg="gray.50" borderRadius="md" p={3}>
              <Text fontSize="sm" mb={1}>
                {t('admin.alerts.simulate.region', 'Region')}: <b>{regionName}</b>
              </Text>
              <Text fontSize="sm" mb={1}>
                {t('admin.alerts.simulate.type', 'Alert type')}: <b>{alertType}</b>
              </Text>
              <Text fontSize="sm">
                {t('admin.alerts.simulate.duration', 'Auto-ends after')}:{' '}
                <b>{t('admin.alerts.simulate.durationMinutes', '{{count}} minutes', { count: durationMinutes })}</b>
              </Text>
            </Box>

            <FormControl>
              <FormLabel fontSize="sm">
                {t('admin.alerts.simulate.typeToConfirm', 'Type the region name to confirm:')}
              </FormLabel>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={regionName}
                data-testid="simulation-confirmation-input"
                autoFocus
              />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} mr={3} variant="ghost">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="orange"
            isDisabled={!canConfirm}
            isLoading={isSubmitting}
            onClick={onConfirm}
            data-testid="simulation-confirmation-confirm"
          >
            {t('admin.alerts.simulate.startButton', 'Start simulation')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
